import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CalendarOptions, EventClickArg, EventInput } from '@fullcalendar/core';
import { CalendarEventDto } from '@shared/dto';
import { calendarEventToCalendarEvent } from '@shared/utils/event-to-calendar.utils';
import { CalendarComponent, CalendarRange } from '@shared/components/calendar/calendar.component';

// Re-exported so existing dashboard imports keep working after the range type moved to
// the shared calendar component.
export type { CalendarRange };

/**
 * Read-only overview calendar for the dashboard.
 *
 * A thin wrapper over the shared {@link CalendarComponent}: it maps operator-wide
 * {@link CalendarEventDto}s to FullCalendar events, keeps the shared look, and adds only
 * the read-only concerns — grow-to-fit height and mapping an event click back to its
 * source DTO ({@link eventClicked}). {@link rangeChanged} is forwarded so the host can
 * (re)load events for the visible window.
 */
@Component({
  selector: 'app-dashboard-calendar',
  standalone: true,
  imports: [CalendarComponent],
  templateUrl: './dashboard-calendar.component.html',
})
export class DashboardCalendarComponent implements OnChanges {
  @Input() events: CalendarEventDto[] | null = [];

  /** When true, dims the calendar and shows a centered spinner while events load. */
  @Input() loading = false;

  @Output() eventClicked = new EventEmitter<CalendarEventDto>();
  @Output() rangeChanged = new EventEmitter<CalendarRange>();

  /** Mapped events bound declaratively to the shared calendar. */
  calendarEvents: EventInput[] = [];

  /** Lookup from FullCalendar event id back to the source DTO (for click emit). */
  private eventsById = new Map<string, CalendarEventDto>();

  /** Read-only surface: grow to fit content, and map event clicks back to the DTO. */
  readonly calendarOptions: CalendarOptions = {
    height: 'auto',
    eventClick: (arg: EventClickArg) => this._onEventClick(arg),
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['events']) return;
    const list = this.events ?? [];
    this.eventsById = new Map(list.map((e) => [`${e.id}`, e]));
    // Feed FullCalendar through its declarative `[events]` input. Mutating the event
    // source imperatively failed to re-render reliably on navigation; a fresh array
    // reference lets the wrapper resync.
    this.calendarEvents = list.map(calendarEventToCalendarEvent);
  }

  private _onEventClick(arg: EventClickArg): void {
    const dto = this.eventsById.get(arg.event.id);
    if (dto) this.eventClicked.emit(dto);
  }
}
